import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { Component } from '@theme/component';

/**
 * Working Plant (Oct 20): keeps the per-variant PDP stock elements in
 * sync when the shopper switches variants — the "Only X left" pill in
 * snippets/wp-product-pills.liquid and the sold-out variant
 * back-in-stock slot in blocks/buy-buttons.liquid.
 *
 * Mirrors assets/product-inventory.js exactly: on the theme's
 * variant:update event, each component morphs its children from the
 * re-rendered section HTML the event carries, so the server-rendered
 * Liquid for the newly selected variant always wins.
 */
class WpVariantSync extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.syncFromVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.syncFromVariantUpdate);
  }

  /**
   * Updates the element from the variant update's re-rendered HTML.
   * @param {import('@theme/events').VariantUpdateEvent} event - The variant update event.
   */
  syncFromVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newElement = event.detail.data.html.querySelector(this.tagName.toLowerCase());

    if (!newElement) return;

    morph(this, newElement, { childrenOnly: true });
  };
}

class WpProductPills extends WpVariantSync {}

class WpBisSlot extends WpVariantSync {}

if (!customElements.get('wp-product-pills')) {
  customElements.define('wp-product-pills', WpProductPills);
}

if (!customElements.get('wp-bis-slot')) {
  customElements.define('wp-bis-slot', WpBisSlot);
}

/**
 * Working Plant (Oct 20, fix round 2): Klaviyo BIS injection guard.
 *
 * Primary fix is the decoy trigger rendered inside <wp-bis-slot> (see
 * blocks/buy-buttons.liquid): Klaviyo adopts a pre-existing
 * .klaviyo-bis-trigger element instead of replacing the add-to-cart
 * button with its own black button. This guard is the backstop in
 * case Klaviyo's onsite script still injects a trigger into the buy
 * row (it targets the DOM Klaviyo is known to touch: the product form
 * inside .buy-buttons-block):
 *   1. any .klaviyo-bis-trigger inside .buy-buttons-block that is not
 *      our decoy gets display:none !important;
 *   2. a foreign leaf element in .product-form-buttons whose entire
 *      text is Klaviyo's button label gets the same treatment (covers
 *      an injected button that does not use the trigger class);
 *   3. if the theme's add-to-cart button (or its component wrapper)
 *      picked up an inline display:none / visibility:hidden, it is
 *      cleared, restoring the theme's grey "Sold out" button.
 * Everything is idempotent and scoped to .buy-buttons-block, so the
 * Gone (For Now) wall's own trigger — rendered outside that wrapper —
 * is never touched. The observer watches childList only, so the
 * guard's own style writes cannot retrigger it.
 */
function wpBisGuardSweep() {
  document
    .querySelectorAll('.buy-buttons-block .klaviyo-bis-trigger:not(.wp-bis-decoy-trigger)')
    .forEach((el) => {
      el.style.setProperty('display', 'none', 'important');
    });

  document.querySelectorAll('.buy-buttons-block .product-form-buttons *').forEach((el) => {
    if (
      el.children.length === 0 &&
      el.textContent.trim().toLowerCase() === 'notify me when available' &&
      !el.closest('.add-to-cart-button') &&
      !el.closest('.wp-bis-decoy')
    ) {
      el.style.setProperty('display', 'none', 'important');
    }
  });

  document
    .querySelectorAll('.buy-buttons-block add-to-cart-component, .buy-buttons-block .add-to-cart-button')
    .forEach((el) => {
      if (el.style.display === 'none') el.style.removeProperty('display');
      if (el.style.visibility === 'hidden') el.style.removeProperty('visibility');
    });
}

if (!window.__wpBisGuardReady) {
  window.__wpBisGuardReady = true;

  const startGuard = () => {
    wpBisGuardSweep();

    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (
            node instanceof Element &&
            (node.closest('.buy-buttons-block') ||
              node.querySelector('.buy-buttons-block, .klaviyo-bis-trigger'))
          ) {
            wpBisGuardSweep();
            return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.body) {
    startGuard();
  } else {
    document.addEventListener('DOMContentLoaded', startGuard, { once: true });
  }
}
